import type { CustomerStatus } from '@clockwise/clients/customers';
import { PillBadge } from '@/components/custom/pill-badge';

/**
 * Pill badge for a customer's active/inactive status.
 *
 * Lives here rather than in the customers list route because it is now used
 * from a plain component too (the subsidiaries panel), and importing it from a
 * route module pulled `createFileRoute` — and the whole router — into every
 * consumer and every test that rendered one.
 *
 * Built on `PillBadge` (not the shadcn `Badge` — see its own comment for
 * why) so this stays pixel-identical to `BillableBadge`, the other place
 * this exact shape/color pairing is reused.
 *
 * Distinct from `LifecycleStatusBadge`, which covers the three-state
 * LifecycleStatus enum used by the activities catalog. Customers only have two.
 */
const STATUS_LABELS: Record<CustomerStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

// Same emerald pill as `LifecycleStatusBadge` (activities, rate cards,
// approval policies, audit log, customer engagements) — solid fill, not the
// translucent `--success` outline this used to carry, so "Active" reads
// identically everywhere it appears rather than being its own one-off shade.
const STATUS_BADGE_STYLES: Record<CustomerStatus, string> = {
  active:
    'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 mt-0.5',
  inactive: 'border-transparent bg-muted text-muted-foreground mt-0.5',
};

export function StatusBadge({ status }: { status: CustomerStatus }) {
  return (
    <PillBadge variant="outline" className={STATUS_BADGE_STYLES[status]}>
      {STATUS_LABELS[status]}
    </PillBadge>
  );
}
